// src/components/Links.js
import React from 'react';
import { motion } from 'framer-motion';
import { 
  ExternalLink, 
  Home, 
  Wallet, 
  Wrench,
  Twitter,
  MessageCircle,
  Headphones,
  Newspaper,
  ChevronRight,
  Hexagon,
  Store 
} from 'lucide-react';
import { useLanguage } from './LanguageContext';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';
import { 
  whataLabLogo, 
  builtOnHedera,
  wsniperIcon,
  wscannerIcon,
  wtraderIcon,
  wclaimerIcon,
  wpanelIcon,
  wcheckerIcon,
  wboostIcon,
  spotifyIcon
} from '../assets';
import './Links.css';

const Links = () => {
  const { t } = useLanguage();

  // Main page links
  const mainLinks = [
    {
      id: 'mainpage',
      icon: <Home size={20} />,
      name: 'Main Page',
      nameEs: 'Página Principal',
      description: 'WhataLab Landing',
      href: 'https://whatalab.io',
      external: true
    },
    {
      id: 'verify',
      icon: <Wallet size={20} />,
      name: 'Verify Wallet',
      nameEs: 'Verificar Wallet',
      description: 'Connect & Verify',
      href: 'https://panel.whatalab.io',
      external: true
    }
  ];

  // Tools links
  const toolsLinks = [
    {
      id: 'sniper',
      icon: wsniperIcon,
      name: 'W·Sniper',
      description: 'NFT Sniper Bot',
      href: 'https://tools.whatalab.io/sniper',
      status: 'Live'
    },
    {
      id: 'orderbook',
      icon: wtraderIcon,
      name: 'W·Trader',
      description: 'Pro Trading Interface',
      href: 'https://tools.whatalab.io/orderbook',
      status: 'Live'
    },
    {
      id: 'scanner',
      icon: wscannerIcon,
      name: 'W·Scanner',
      description: 'Market Analytics',
      href: 'https://tools.whatalab.io/tracker',
      status: 'Live'
    },
    {
      id: 'claimdrop',
      icon: wclaimerIcon,
      name: 'W·ClaimDrop',
      description: 'Token Distribution',
      href: 'https://tools.whatalab.io/air-tool',
      status: 'Live'
    },
    {
      id: 'boost',
      icon: wboostIcon,
      name: 'W·Boost',
      description: 'Utility Token',
      href: 'https://tools.whatalab.io/buy',
      status: 'Live'
    }
  ];

  // Social & Community links
  const socialLinks = [
    {
      id: 'twitter',
      icon: <Twitter size={20} />,
      name: 'Twitter / X',
      description: '@WhataLab_',
      href: 'https://x.com/WhataLab_',
      color: '#1DA1F2'
    },
    {
      id: 'discord',
      icon: <MessageCircle size={20} />,
      name: 'Discord',
      description: 'Join Community',
      href: 'https://discord.gg/HxbEqFRFjW',
      color: '#5865F2'
    },
    {
      id: 'spotify',
      icon: spotifyIcon,
      name: 'Podcast',
      description: 'WhataLab Show',
      href: 'https://open.spotify.com/show/3uYiIIzsNWXQbFQLTJTRBq',
      color: '#1DB954'
    },
    {
      id: 'news',
      icon: <Newspaper size={20} />,
      name: 'WhataNews',
      description: 'Latest Updates',
      href: '/whatanews',
      color: '#26E2B3'
    }
  ];

  // Ecosystem links
  const ecosystemLinks = [
    {
      id: 'Kabila',
      icon: <Store size={20} />,
      name: 'Kabila',
      description: 'Kabila marketplace',
      href: 'https://market.kabila.app/es/collections/9219990/items',
      external: true
    },
    {
      id: 'sentx',
      icon: <Store size={20} />,
      name: 'SentX',
      description: 'SentX Marketplace',
      href: 'https://sentx.io/nft-marketplace/whatakit',
      external: true
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  const renderLinkCard = (link, isToolLink = false) => {
    const isExternal = link.external || link.href.startsWith('http');
    
    return (
      <motion.a
        key={link.id}
        href={link.href}
        target={isExternal ? '_blank' : '_self'}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className={`links-card ${isToolLink ? 'links-card--tool' : ''}`}
        variants={itemVariants}
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="links-card__icon">
          {typeof link.icon === 'string' ? (
            <img src={link.icon} alt={link.name} className="links-card__icon-img" />
          ) : (
            link.icon
          )}
        </div>
        <div className="links-card__content">
          <span className="links-card__name">{link.name}</span>
          <span className="links-card__description">{link.description}</span>
        </div>
        {link.status && (
          <span className={`links-card__status links-card__status--${link.status.toLowerCase()}`}>
            {link.status}
          </span>
        )}
        <ChevronRight size={18} className="links-card__arrow" />
      </motion.a>
    );
  };

  return (
    <div className="links-page">
      {/* Background Effects */}
      <div className="links-bg">
        <div className="links-bg__gradient" />
        <div className="links-bg__grid" />
        <div className="links-bg__glow links-bg__glow--1" />
        <div className="links-bg__glow links-bg__glow--2" />
      </div>

      {/* Header Controls */}
      <div className="links-controls">
        <ThemeToggle />
        <LanguageToggle />
      </div>

      {/* Main Content */}
      <motion.div 
        className="links-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Profile Header */}
        <motion.header className="links-header" variants={itemVariants}>
          <div className="links-header__logo">
            <img src={whataLabLogo} alt="WhataLab" />
          </div>
          <h1 className="links-header__title">WhataLab</h1>
          <p className="links-header__subtitle">
            Premium Trading Tools for Hedera
          </p>
        </motion.header>

        {/* Main Links Section */}
        <motion.section className="links-section" variants={itemVariants}>
          <h2 className="links-section__title">
            <Home size={16} />
            Main
          </h2>
          <div className="links-grid">
            {mainLinks.map(link => renderLinkCard(link))}
          </div>
        </motion.section>

        {/* Tools Section */}
        <motion.section className="links-section" variants={itemVariants}>
          <h2 className="links-section__title">
            <Wrench size={16} />
            Tools
          </h2>
          <div className="links-grid links-grid--tools">
            {toolsLinks.map(link => renderLinkCard(link, true))}
          </div>
        </motion.section>

        {/* Social Section */}
        <motion.section className="links-section" variants={itemVariants}>
          <h2 className="links-section__title">
            <MessageCircle size={16} />
            Community
          </h2>
          <div className="links-grid links-grid--social">
            {socialLinks.map(link => (
              <motion.a
                key={link.id}
                href={link.href}
                target={link.href.startsWith('http') ? '_blank' : '_self'}
                rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="links-card links-card--social"
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                style={{ '--social-color': link.color }}
              >
                <div className="links-card__icon">
                  {typeof link.icon === 'string' ? (
                    <img src={link.icon} alt={link.name} className="links-card__icon-img" />
                  ) : (
                    link.icon
                  )}
                </div>
                <div className="links-card__content">
                  <span className="links-card__name">{link.name}</span>
                  <span className="links-card__description">{link.description}</span>
                </div>
                <ExternalLink size={16} className="links-card__arrow" />
              </motion.a>
            ))}
          </div>
        </motion.section>

        {/* Ecosystem Section */}
        <motion.section className="links-section" variants={itemVariants}>
          <h2 className="links-section__title">
            <Hexagon size={16} />
            Markets
          </h2>
          <div className="links-grid">
            {ecosystemLinks.map(link => renderLinkCard(link))}
          </div>
        </motion.section>

        {/* Footer */}
        <motion.footer className="links-footer" variants={itemVariants}>
          <a 
            href="https://hedera.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="links-footer__badge"
          >
            <img src={builtOnHedera} alt="Built on Hedera" />
          </a>
          <p className="links-footer__copyright">
            © {new Date().getFullYear()} WhataLab. All rights reserved.
          </p>
        </motion.footer>
      </motion.div>
    </div>
  );
};

export default Links;
